import React from 'react';
import {Typography, Tooltip, Tag} from 'antd';
import {Link, useRouteMatch} from 'react-router-dom';
import {isObject} from 'lodash';

// POSIX-style path resolver, scoped to the two call sites below. Replaces
// `resolvePath()` from Node's `path` module, which Vite refuses to
// polyfill for the browser ("Module 'path' has been externalized for
// browser compatibility"). Webpack auto-polyfilled, so this only mattered
// once the editor started building under Vite (portal-mounted mode). Past
// incident (gap #12 from portal Day 11): clicking any model with a $ref
// threw a runtime error and rendered the model view blank.
//
// Behavior we need: combine `base` (a directory-ish path) with `rel`
// (`./foo`, `../bar/baz`, `foo`) and normalize `.` / `..` segments. We do
// NOT need cwd-anchored resolution, drive letters, or any of Path.resolve's
// real semantics — both call sites pass a fully-formed prefix and a YAML
// $ref tail.
function resolvePath(base, rel) {
    const isAbsolute = base.startsWith('/');
    const segments = base.split('/').filter(Boolean);
    for (const seg of rel.split('/')) {
        if (seg === '' || seg === '.') continue;
        if (seg === '..') {
            segments.pop();
        } else {
            segments.push(seg);
        }
    }
    return (isAbsolute ? '/' : '') + segments.join('/');
}

// Small badge so users can tell at a glance whether a ref points into
// this spec or another file. Local refs get nothing — they're the
// common case. Cross-spec refs (./foo.yml, ../bar.yml, http://...) are
// labelled "External". Unresolved refs (target missing or backend
// resolve failed) are labelled "Unresolved" in red so the user knows
// the body that follows isn't actually empty — there's content the
// editor can't reach.
function RefBadge({external, unresolved}) {
    if (unresolved) return <Tag color="red" style={{marginLeft: 4}}>Unresolved</Tag>;
    if (external) return <Tag style={{marginLeft: 4}}>External</Tag>;
    return null;
}

// Display the component key verbatim — it's already the OpenAPI JSON key
// (e.g. "accessorialCosts.unitBreaks"). Older versions snake_cased for URL
// slug and camelCased for display, which collapsed dots and made keys
// unreadable. After the schemas() extractor change (key === name) both
// transforms are wrong; just use the key as-is for URL and for text.
function Ref({link, params, componentType = 'models', unresolved}) {
    link = link.$ref || link;

    if (isObject(link)) {
        return null;
    }

    if (!link || Object.keys(link).length === 0 || link.properties) {
        return null;
    }

    if (link.indexOf('#/components') === 0) {
        const modelKey = link.substring(12).split('/').pop();
        const url = `/${params.env}/standards/${params.category}/${params.key}/${params.version}/${componentType}/${modelKey}`;
        return (
            <Link to={url}>
                <Tooltip title={link}>
                    <Typography.Text code>{componentType !== 'models' ? componentType + '/' + modelKey : modelKey}</Typography.Text>
                </Tooltip>
                <RefBadge unresolved={unresolved} />
            </Link>
        );
    } else {
        const specPath = link.substring(0, link.indexOf('#'));
        const modelKey = link.split('/').pop();
        if (specPath.indexOf('./') === 0 || specPath.indexOf('../') === 0) {
            let targetPath = resolvePath(`/${params.env}/standards/${params.category}`, specPath);
            if (targetPath.indexOf('.') !== -1) {
                targetPath = targetPath.substring(0, targetPath.indexOf('.'));
            }
            const url = `${targetPath}/latest/${componentType}/${modelKey}`;
            return (
                <Link to={url}>
                    <Tooltip title={link}>
                        <Typography.Text code>{componentType !== 'models' ? componentType + '/' + modelKey : modelKey}</Typography.Text>
                    </Tooltip>
                    <RefBadge external unresolved={unresolved} />
                </Link>
            );
        } else {
            let targetPath = resolvePath(`${params.env}/standards/${params.category}/${params.key}`, specPath);
            const url = `${targetPath}/latest/${componentType}/${modelKey}`;
            return (
                <a href={url}>
                    <Tooltip title={link}>
                        <Typography.Text code>{componentType !== 'models' ? componentType + '/' + modelKey : modelKey}</Typography.Text>
                    </Tooltip>
                    <RefBadge external unresolved={unresolved} />
                </a>
            );
        }
    }
}

export function OpenAPIRef({link, componentType = 'models', unresolved}) {
    const {params} = useRouteMatch('/:env/standards/:category/:key/:version');

    if (!link) {
        return null;
    }
    if (Array.isArray(link)) {
        return (
            <ul>
                {link.map((l) =>
                    l.$ref ? (
                        <li key={l.$ref} style={{marginRight: 5}}>
                            <Ref link={l} params={params} unresolved={unresolved} />
                        </li>
                    ) : null
                )}
            </ul>
        );
    } else {
        return <Ref link={link} params={params} componentType={componentType} unresolved={unresolved} />;
    }
}
